import type { Option } from "../core/option.ts";
import type { CellSnapshot } from "./mutation-backend.ts";

/**
 * One minimal store write the projection emits to carry a cell store from a
 * previous derived snapshot to the next: upsert `value` for `id`, or remove it
 * (`value == null`). `prev` is the value `id` held in the PREVIOUS derived
 * snapshot — the projection's own record of what it last wrote, not the caller's
 * notion of the prior event — so the sink clears exactly the cell that is actually
 * there (old lane / old date). Feed each write to the domain's single
 * lane-placement authority (events: `EventsTable.writeCanonical(key, value, id,
 * prev)`); the projection decides only WHAT changed, never WHERE a cell lands.
 */
export interface ProjectionWrite<Item, Id extends string> {
  readonly value: Option<Item>;
  readonly id: Id;
  readonly prev: Option<Item>;
}

/**
 * Diff two derived snapshots (`base ⊕ overlay`, before and after) into the minimal
 * set of `ProjectionWrite`s that reconcile a cell store from `prev` to `next`.
 *
 * This is the read half of the overlay substrate. Instead of each mutation writing
 * one cell incrementally — and the store slowly drifting from what `derive()`
 * actually says (the "index vs store" / lane-drift split-brain the Move 1–4 work
 * exists to delete) — the overlay re-derives the whole per-row snapshot and the
 * store is reconciled *to* it. One writer, fed from one derivation, so the store
 * can never disagree with the derived truth; lane correctness lives in exactly one
 * place (the sink), reached with the projection's own `prev`, never a second
 * source of truth.
 *
 * Generic over `Item`; `equal` is the domain's cell-equality (events:
 * `eventSignature`; tasks: field compare) so an unchanged id in a fresh derive
 * emits no write and the store doesn't churn (no redundant `ColumnDataChanged`).
 *
 * Order: removals first, then upserts — a removed id's lane is cleared before any
 * write that re-adds a *different* id at the same position is applied.
 */
export function diffSnapshots<Item, Id extends string>(
  prev: CellSnapshot<Item, Id>,
  next: CellSnapshot<Item, Id>,
  equal: (a: Item, b: Item) => boolean,
): ProjectionWrite<Item, Id>[] {
  const writes: ProjectionWrite<Item, Id>[] = [];
  for (const [id, prevItem] of prev) {
    if (!next.has(id)) writes.push({ value: null, id, prev: prevItem });
  }
  for (const [id, item] of next) {
    const prevItem = prev.get(id) ?? null;
    if (prevItem == null || !equal(prevItem, item)) {
      writes.push({ value: item, id, prev: prevItem });
    }
  }
  return writes;
}
